<template>
  <div ref="containerRef" class="liquid-gradient"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import * as THREE from "three";

const containerRef = ref<HTMLDivElement | null>(null);

let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.OrthographicCamera | null = null;
let material: THREE.ShaderMaterial | null = null;
let animationFrame = 0;

const vertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;

  uniform float uTime;
  uniform vec2 uResolution;
  varying vec2 vUv;

  vec3 surface = vec3(0.941176, 0.964706, 0.972549);
  vec3 blobColorA = vec3(0.62, 0.82, 1.00);
  vec3 blobColorB = vec3(0.78, 0.68, 1.00);

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);

    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));

    vec2 u = f * f * (3.0 - 2.0 * f);

    return mix(a, b, u.x)
      + (c - a) * u.y * (1.0 - u.x)
      + (d - b) * u.x * u.y;
  }

  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;

    for (int i = 0; i < 6; i++) {
      value += amplitude * noise(p);
      p *= 2.0;
      amplitude *= 0.5;
    }

    return value;
  }

  float blob(vec2 p, float angle, float phase, float size) {
    float progress = fract(uTime * 0.055 + phase);
    float easedProgress = smoothstep(0.0, 1.0, progress);
    vec2 direction = vec2(cos(angle), sin(angle));
    vec2 center = direction * easedProgress * 1.25;
    vec2 local = p - center;

    float distortion = fbm(local * 3.0 + uTime * 0.08 + phase * 12.0);
    local += direction * (distortion - 0.5) * 0.16;

    float shape = 1.0 - smoothstep(size * 0.18, size, length(local));
    float appear = smoothstep(0.0, 0.16, progress);
    float disappear = 1.0 - smoothstep(0.62, 1.0, progress);

    return shape * appear * disappear;
  }

  void main() {
    vec2 uv = vUv;

    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 p = (uv - 0.5) * aspect;
    p.y *= 1.12;

    float field = 0.0;
    field += blob(p, -0.35, 0.00, 0.42);
    field += blob(p, 0.95, 0.18, 0.34);
    field += blob(p, 2.35, 0.36, 0.38);
    field += blob(p, 3.55, 0.54, 0.32);
    field += blob(p, 5.05, 0.72, 0.36);

    float softField = smoothstep(0.04, 0.78, field);
    float centerGlow = (1.0 - smoothstep(0.0, 0.76, length(p))) * 0.18;
    float verticalFade = smoothstep(0.0, 0.2, uv.y) * smoothstep(0.0, 0.2, 1.0 - uv.y);
    float horizontalFade = smoothstep(0.0, 0.08, uv.x) * smoothstep(0.0, 0.08, 1.0 - uv.x);
    float edgeFade = verticalFade * horizontalFade;

    vec3 blobColor = mix(blobColorA, blobColorB, smoothstep(-0.25, 0.45, p.y));
    vec3 color = mix(surface, blobColor, clamp((softField * 0.74 + centerGlow) * edgeFade, 0.0, 1.0));

    float grain = (hash(uv * uResolution + uTime) - 0.5) * 0.01;
    color += grain * edgeFade;

    gl_FragColor = vec4(color, 1.0);
  }
`;

onMounted(() => {
  const container = containerRef.value;
  if (!container) return;

  scene = new THREE.Scene();

  camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  renderer = new THREE.WebGLRenderer({
    antialias: false,
    alpha: true,
    powerPreference: "high-performance",
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth, container.clientHeight);
  container.appendChild(renderer.domElement);

  material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTime: { value: 0 },
      uResolution: {
        value: new THREE.Vector2(container.clientWidth, container.clientHeight),
      },
    },
  });

  const geometry = new THREE.PlaneGeometry(2, 2);
  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  const resize = () => {
    if (!renderer || !material || !container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    renderer.setSize(width, height);
    material.uniforms.uResolution.value.set(width, height);
  };

  const animate = () => {
    if (!renderer || !scene || !camera || !material) return;

    material.uniforms.uTime.value += 0.016;
    renderer.render(scene, camera);

    animationFrame = requestAnimationFrame(animate);
  };

  window.addEventListener("resize", resize);
  animate();

  onBeforeUnmount(() => {
    window.removeEventListener("resize", resize);
  });
});

onBeforeUnmount(() => {
  cancelAnimationFrame(animationFrame);

  if (renderer) {
    renderer.dispose();
    renderer.domElement.remove();
  }

  material?.dispose();

  renderer = null;
  scene = null;
  camera = null;
  material = null;
});
</script>

<style scoped>
.liquid-gradient {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #fafafa;
}

.liquid-gradient canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
